// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { instanceToPlain, plainToInstance } from 'class-transformer';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Service imports
import { RPCacheRegistrationService } from '../../../_services/shared-services/rp-cache-registration-service';

// Model imports
import { BaseResidentParameterDto } from '../../../_models/base-resident-parameter-dto';
import { OwnerInfoHistoryResultDto } from '../../../_models/owner-info-history-result-dto';
import { ResultStatusDto } from '../../../_models/result-status-dto';
import { AddEditContactParameterDto } from '../../_models/add-edit-contact-parameter-dto';
import { ChangeContactSequenceParameterDto } from '../../_models/change-contact-sequence-parameter-dto';
import { ContactEditorParameterDto } from '../../_models/contact-editor-parameter-dto';
import { ContactEditorResultDto } from '../../_models/contact-editor-result-dto';
import { ContactInfoEditorResultDto } from '../../_models/contact-info-editor-result-dto';
import { ContactInfoParametersDto } from '../../_models/contact-info-parameters-dto';
import { ContactInfoResultDto } from '../../_models/contact-info-result-dto';
import { DeleteContactParameterDto } from '../../_models/delete-contact-parameter-dto';
import { MailingAddressResultDto } from '../../_models/mailing-address-result-dto';
import { OwnerInfoResultDto } from '../../_models/owner-information/owner-info-result-dto';
import { UpdateOwnerInfoParameterDto } from '../../_models/owner-information/update-owner-info-parameter-dto';
import { PropertyInfoResultDto } from '../../_models/property-info-result-dto';
import { PropertyPublishInfoRawDto } from '../../_models/property-publish-info-raw-dto';
import { ResidentRuleResultDto } from '../../_models/resident-rule-result-dto';
import { UpdateMailingAddressParameterDto } from '../../_models/update-mailing-address-parameter-dto';

// Service imports
import { EnvironmentConfigService } from '../../../_services/environment-config.service';

// Constants imports
import { CacheDuration } from '../../../_constants/cache-duration';

@Injectable({
  providedIn: 'root'
})
export class InformationService {
  constructor(
    private httpClient: HttpClient,
    private cacheRegistrationService: RPCacheRegistrationService,
    private envConfig: EnvironmentConfigService
  ) {}

  getContactInformation(
    param: BaseResidentParameterDto
  ): Observable<ContactInfoResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/ci' +
      param.getQueryString();
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get(url, options)
      .pipe(
        map((responseData: any) =>
          plainToInstance(ContactInfoResultDto, responseData)
        )
      );
  }

  deleteContactInformation(
    param: DeleteContactParameterDto
  ): Observable<ResultStatusDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/ci' +
      param.getQueryString();
    return this.httpClient
      .delete(url)
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResultStatusDto, responseData)
        )
      );
  }

  updateContactSequence(
    param: ChangeContactSequenceParameterDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .put(this.envConfig.ccResidentPortalApiUrl + 'account/ci/sequence', param)
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResultStatusDto, responseData)
        )
      );
  }

  getPhoneEmailEditorData(
    param: BaseResidentParameterDto
  ): Observable<ContactInfoEditorResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/ci/phoneemails/editor' +
      param.getQueryString();
    return this.httpClient
      .get(url)
      .pipe(
        map((responseData: any) =>
          plainToInstance(ContactInfoEditorResultDto, responseData)
        )
      );
  }

  updateContactDetails(
    param: ContactInfoParametersDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .post(
        this.envConfig.ccResidentPortalApiUrl + 'account/ci/phoneemails',
        param
      )
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResultStatusDto, responseData)
        )
      );
  }

  getContactInfoEditorData(
    param: ContactEditorParameterDto
  ): Observable<ContactEditorResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/ci/editor' +
      param.getQueryString();
    return this.httpClient
      .get(url)
      .pipe(
        map((responseData: any) =>
          plainToInstance(ContactEditorResultDto, responseData)
        )
      );
  }

  saveContactInformation(
    param: AddEditContactParameterDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .post(this.envConfig.ccResidentPortalApiUrl + 'account/ci', param)
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResultStatusDto, responseData)
        )
      );
  }

  getPropertyInformation(
    param: BaseResidentParameterDto
  ): Observable<PropertyInfoResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/propertyinfo' +
      param.getQueryString();
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get(url, options)
      .pipe(
        map((responseData: any) =>
          plainToInstance(PropertyInfoResultDto, responseData)
        )
      );
  }

  getAssessmentRules(
    param: BaseResidentParameterDto
  ): Observable<ResidentRuleResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/rules/assessment' +
      param.getQueryString();
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get(url, options)
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResidentRuleResultDto, responseData)
        )
      );
  }

  getLateFeeRules(
    param: BaseResidentParameterDto
  ): Observable<ResidentRuleResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/rules/latefee' +
      param.getQueryString();
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get(url, options)
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResidentRuleResultDto, responseData)
        )
      );
  }

  getMailingAddressInfo(
    param: BaseResidentParameterDto
  ): Observable<MailingAddressResultDto> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/ownerinfo/mailingaddress' +
      param.getQueryString();
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get(url, options)
      .pipe(
        map((responseData: any) =>
          plainToInstance(MailingAddressResultDto, responseData)
        )
      );
  }

  getMailingAddressHistory(
    param: BaseResidentParameterDto
  ): Observable<OwnerInfoHistoryResultDto[]> {
    const url = `${this.envConfig.ccResidentPortalApiUrl}account/ownerinfo/mailingaddress/history${param.getQueryString()}`;
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get<any[]>(url, options)
      .pipe(
        map((responseData: any[]) =>
          plainToInstance(OwnerInfoHistoryResultDto, responseData)
        )
      );
  }

  updateMailingAddress(
    param: UpdateMailingAddressParameterDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .put(
        this.envConfig.ccResidentPortalApiUrl +
          'account/ownerinfo/mailingaddress/update',
        param
      )
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResultStatusDto, responseData)
        )
      );
  }

  getSharingEditorInfo(param: BaseResidentParameterDto): Observable<any> {
    const url =
      this.envConfig.ccResidentPortalApiUrl +
      'account/publish/editor' +
      param.getQueryString();
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get(url, options)
      .pipe(
        map((responseData: any) =>
          plainToInstance(PropertyPublishInfoRawDto, responseData)
        )
      );
  }

  saveSharingInfo(
    param: PropertyPublishInfoRawDto
  ): Observable<ResultStatusDto> {
    return this.httpClient
      .put(this.envConfig.ccResidentPortalApiUrl + 'account/publish', param)
      .pipe(
        map((responseData: any) =>
          plainToInstance(ResultStatusDto, responseData)
        )
      );
  }

  // ----- Owner Information ----- //
  getOwnerInformation(
    param: BaseResidentParameterDto
  ): Observable<OwnerInfoResultDto> {
    const url = `${this.envConfig.ccResidentPortalApiUrl}account/ownerinfo${param.getQueryString()}`;
    this.cacheRegistrationService.registerForCache(url, param.clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };

    return this.httpClient
      .get(url, options)
      .pipe(
        map((responseData: any) =>
          plainToInstance(OwnerInfoResultDto, responseData)
        )
      );
  }

  updateOwnerInformation(
    param: UpdateOwnerInfoParameterDto
  ): Observable<ResultStatusDto> {
    const url = `${this.envConfig.ccResidentPortalApiUrl}account/ownerinfo`;
    return this.httpClient
      .put<ResultStatusDto>(url, instanceToPlain(param))
      .pipe(map((response) => plainToInstance(ResultStatusDto, response)));
  }
}
